import Button from "@material-ui/core/Button";
import TextareaAutosize from "@material-ui/core/TextareaAutosize";
import "emoji-mart/css/emoji-mart.css";
import React, { ChangeEvent, FC, ReactElement, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
// import { profanity } from '@2toad/profanity';
import { useParams } from "react-router-dom";
import imageCompression from "browser-image-compression";
import { MAX_TEXT_LENGTH } from "../../constants/common-constants";
import { usePopover } from "../../context/PopoverContext";
import { useInputText } from "../../hook/useInputText";
import { TweetApi } from "../../services/api/tweet-service/tweetApi";
import { setOpenSnackBar } from "../../store/ducks/actionSnackbar/actionCreators";
import { resetAddTweetFormState, setImages, setScheduleDate } from "../../store/ducks/addTweetForm/actionCreators";
import {
    selectGif,
    selectImageDescription,
    selectImages,
    selectPollData,
    selectReplyType,
    selectScheduledDate,
    selectSelectedUsers,
    selectVisiblePoll
} from "../../store/ducks/addTweetForm/selector";
import { fetchReplyTweet } from "../../store/ducks/tweet/actionCreators";
import {
    addPoll,
    addQuoteTweet,
    addScheduledTweet,
    addTweet,
    setTweetSentState,
    updateScheduledTweet
} from "../../store/ducks/tweets/actionCreators";
import { TweetSentState } from "../../store/ducks/tweets/contracts/state";
import {
    selectIsTweetSentStateError,
    selectIsTweetSentStateSent,
    selectTweetErrorMessage
} from "../../store/ducks/tweets/selectors";
import { fetchUserMentionLists, resetUsersMentionsState } from "../../store/ducks/usersMentions/actionCreators";
import { selectUsersMentionsList } from "../../store/ducks/usersMentions/selectors";
import { Image } from "../../types/common";
import { BaseListResponse } from "../../types/lists";
import { QuoteTweetResponse, TweetResponse } from "../../types/tweet";
import { formatScheduleDate } from "../../util/format-date-helper";
import { debounce } from "../../util/utils-helper";
import GifImage from "../GifImage/GifImage";
import Quote from "../Quote/Quote";
import TweetListComponent from "../TweetListComponent/TweetListComponent";
import UploadImages from "../UploadImages/UploadImages";
import { useAddTweetFormStyles } from "./AddTweetFormStyles";
import AddTweetImage from "./AddTweetImage/AddTweetImage";
import EmojiIconButton from "./EmojiIconButton/EmojiIconButton";
import GifIconButton from "./GifIconButton/GifIconButton";
import MentionsList from "./MentionsList/MentionsLIst";
import Poll from "./Poll/Poll";
import PollIconButton from "./PollIconButton/PollIconButton";
import ProfileAvatar from "./ProfileAvatar/ProfileAvatar";
import Reply from "./Reply/Reply";
import ScheduleDateInfo from "./ScheduleDateInfo/ScheduleDateInfo";
import ScheduleIconButton from "./ScheduleIconButton/ScheduleIconButton";
import TextCountProgress from "./TextCountProgress/TextCountProgress";
import { selectUserDataId } from "../../store/ducks/user/selectors";
import { ActionSnackbarStateType } from "../../store/ducks/actionSnackbar/contracts/state";

export interface AddTweetFormProps {
    unsentTweet?: TweetResponse;
    quoteTweet?: QuoteTweetResponse;
    tweetList?: BaseListResponse;
    maxRows?: number;
    minRows?: number;
    tweetId?: string;
    title: string;
    buttonName: string;
    addressedUsername?: string;
    addressedId?: string;
    onCloseModal?: () => void;
    isExplore?: boolean;
}

export interface ImageObj {
    src: string;
    file: File;
}

const AddTweetForm: FC<AddTweetFormProps> = ({
    unsentTweet,
    quoteTweet,
    tweetList,
    maxRows,
    minRows,
    tweetId,
    title,
    buttonName,
    addressedUsername,
    addressedId,
    onCloseModal,
    isExplore
}): ReactElement => {
    const classes = useAddTweetFormStyles();
    const dispatch = useDispatch();
    const params = useParams<{ userId: string }>();
    const visiblePoll = useSelector(selectVisiblePoll);
    const pollData = useSelector(selectPollData);
    const gif = useSelector(selectGif);
    const scheduledDate = useSelector(selectScheduledDate);
    const replyType = useSelector(selectReplyType);
    const imageDescription = useSelector(selectImageDescription);
    const images = useSelector(selectImages);
    const selectedUsers = useSelector(selectSelectedUsers);
    const usersMentionsList = useSelector(selectUsersMentionsList);
    const [cursorPosition, setCursorPosition] = useState<number>(0);
    const { text, setText, addEmoji, textConverter } = useInputText();
    const { setAnchorEl } = usePopover();
    const isTweetSentStateSent = useSelector(selectIsTweetSentStateSent);
    const isTweetSentStateError = useSelector(selectIsTweetSentStateError);
    const tweetErrorMessage = useSelector(selectTweetErrorMessage);
    const myProfileId = useSelector(selectUserDataId);
    const [isTweetBtnDisabled, setIsTweetBtnDisabled] = useState<boolean>(false);
    const [tweetImages, setTweetImages] = useState<ImageObj[]>([]);

    useEffect(() => {
        dispatch(resetUsersMentionsState());
    }, []);

    useEffect(() => {
        if (isTweetSentStateSent) {
            dispatch(
                setOpenSnackBar({
                    message: scheduledDate
                        ? `Your Tweet will be sent on ${formatScheduleDate(scheduledDate)}`
                        : "Your Tweet was sent."
                })
            );
        } else if (isTweetSentStateError) {
            dispatch(
                setOpenSnackBar({
                    message: tweetErrorMessage || "Oops! Something went wrong!",
                    type: tweetErrorMessage ? ActionSnackbarStateType.SUCCESS : ActionSnackbarStateType.ERROR
                })
            );
        }

        return () => {
            dispatch(setTweetSentState(TweetSentState.IDEL));
        };
    }, [isTweetSentStateSent, isTweetSentStateError, tweetErrorMessage]);

    useEffect(() => {
        if (unsentTweet) {
            setText(unsentTweet.text);
            dispatch(setScheduleDate(new Date(unsentTweet.scheduledDate)));
            if (unsentTweet.images?.length !== 0) {
                const newImages = [...images];
                const newImage = { ...images[0] };
                newImage.src = unsentTweet.images[0].src;
                newImages[0] = newImage;
                dispatch(setImages(newImages));
            }
        }
    }, [unsentTweet]);

    const handleClickAddTweet = async (): Promise<void> => {
        const tweet = await tweetPreProcessing();
        if (tweet) {
            if (visiblePoll) {
                const { day, hour, minute, choice1, choice2, choice3, choice4 } = pollData;
                const pollDateTime = day * 1440 + hour * 60 + minute;
                const choices = [choice1, choice2, choice3, choice4].filter((item) => item);
                dispatch(addPoll({ ...tweet, pollDateTime, choices }));
            } else if (scheduledDate !== null && unsentTweet === undefined) {
                dispatch(addScheduledTweet({ ...tweet, scheduledDate }));
            } else if (unsentTweet) {
                dispatch(updateScheduledTweet({ ...tweet, id: unsentTweet?.id }));
                if (onCloseModal) onCloseModal();
            } else {
                dispatch(addTweet(tweet));
            }
            if (onCloseModal) onCloseModal();
            tweetPostProcessing();
        }
    };

    const handleClickQuoteTweet = async (): Promise<void> => {
        const tweet = await tweetPreProcessing();
        if (tweet) {
            dispatch(addQuoteTweet({ ...tweet, tweetId: quoteTweet!.id, userId: params.userId ?? myProfileId }));
            tweetPostProcessing();
        }
    };

    const handleClickReplyTweet = async (): Promise<void> => {
        const tweet = await tweetPreProcessing();
        if (tweet) {
            dispatch(
                fetchReplyTweet({
                    ...tweet,
                    tweetId: tweetId!,
                    userId: params.userId ?? myProfileId,
                    addressedUsername: addressedUsername!,
                    addressedId: addressedId!,
                    isExplore
                })
            );
            tweetPostProcessing();
        }
    };

    const tweetPreProcessing = async () => {
        setIsTweetBtnDisabled(true);
        const text = textConverter();
        // const isCensoredText = profanity.exists(text);
        // if (isCensoredText) {
        //     dispatch(setOpenSnackBar("Please do not use offensive/vulgar in your tweet."));
        //     return false;
        // }
        const options = {
            maxSizeMB: 2, // Approximate size in MB
            maxWidthOrHeight: 1920, // Adjust resolution as needed
            useWebWorker: true // For faster processing
        };
        const compressedImage = [];
        for (const image of tweetImages) {
            const file = image.file;
            if (file && file?.type.includes("image")) {
                // Only compress if file size > 2MB
                if (file.size > 2 * 1024 * 1024) {
                    try {
                        const compressedFile = await imageCompression(file, options);
                        compressedImage.push(compressedFile);
                    } catch (error) {
                        console.error("Error compressing image:", error);
                    }
                } else {
                    compressedImage.push(file);
                }
            } else {
                if (file?.type.includes("video") && file?.size <= 10 * 1024 * 1024) {
                    compressedImage.push(file);
                } else {
                    dispatch(
                        setOpenSnackBar({
                            message: `${file?.name || "Unknown"} file size more than 10 MB`,
                            type: ActionSnackbarStateType.ERROR
                        })
                    );
                    continue;
                }
            }
        }
        const formData = new FormData();
        compressedImage.forEach((file) => {
            formData.append("file", file, file.name);
        });
        let result: any = []; // Adjust type if needed
        try {
            if (compressedImage?.length) {
                const { data } = await TweetApi.uploadTweetImage(formData);
                result = data;
            }
        } catch (error) {
            if (error?.response?.data?.message) {
                dispatch(
                    setOpenSnackBar({
                        message: error?.response?.data?.message || "Oops! Something went wrong!",
                        type: ActionSnackbarStateType.ERROR
                    })
                );
            }
            setIsTweetBtnDisabled(false); // Re-enable the button
            return false;
        }
        const taggedImageUsers = selectedUsers.map((user) => user.id);
        return {
            text,
            listId: tweetList?.id,
            gifImage: gif?.images.downsized,
            images: result,
            imageDescription,
            taggedImageUsers,
            replyType
        };
    };

    const tweetPostProcessing = (): void => {
        setTweetImages([]);
        dispatch(resetAddTweetFormState());
        dispatch(resetUsersMentionsState());
        setText("");
        if (onCloseModal) onCloseModal();
        setIsTweetBtnDisabled(false);
    };

    // Debounced function to find the @mentions
    const findMentions = useCallback(
        debounce((inputText: string, cursor: number) => {
            // Find the last word that starts with @ and doesn't include a space
            const match = RegExp(/@\w*$/).exec(inputText.slice(0, cursor));
            if (match) {
                const query = match[0].substring(1); // remove the @
                if (query.length > 0) {
                    // Filter users by query
                    dispatch(resetUsersMentionsState());
                    dispatch(fetchUserMentionLists({ username: query.toLowerCase() }));
                }
            }
        }, 300),
        []
    );

    const handleChangeText = useCallback(
        (event: ChangeEvent<HTMLTextAreaElement>): void => {
            setAnchorEl(event.currentTarget);

            const inputText = event.target.value;
            setText(event.target.value);

            const cursor = event.target.selectionStart;
            setCursorPosition(cursor);

            // Call the debounced mention search function
            findMentions(inputText, cursor);
        },
        [dispatch]
    );

    // Handle selecting a suggestion
    const handleSelect = (username: string): void => {
        const beforeCursor = text.slice(0, cursorPosition);
        const afterCursor = text.slice(cursorPosition);

        // Replace the last @mention in the text with the selected username
        const newText = beforeCursor.replace(/@\w*$/, `@${username} `);
        setText(newText + afterCursor);

        dispatch(resetUsersMentionsState());
    };

    const tweetDisabled = useCallback(() => {
        if (visiblePoll) {
            return !pollData.choice1 || !pollData.choice2 || !text || text.length > MAX_TEXT_LENGTH;
        } else if (isTweetBtnDisabled) {
            return true;
        } else {
            return !text || text.length > MAX_TEXT_LENGTH;
        }
    }, [visiblePoll, pollData, text, isTweetBtnDisabled]);
    
    const handleImage = (index: number ) => {
        const tmpImages = tweetImages;
        tmpImages.splice(index, 1);
        setTweetImages(tmpImages)
    }

    return (
        <>
            <div className={classes.content}>
                <ProfileAvatar />
                <div className={classes.textareaWrapper}>
                    <ScheduleDateInfo />
                    <TextareaAutosize
                        onChange={handleChangeText}
                        className={classes.contentTextarea}
                        placeholder={visiblePoll ? "Ask a question..." : title}
                        value={text}
                        maxRows={maxRows}
                        minRows={tweetImages?.length !== 0 ? 1 : minRows}
                    />
                    <MentionsList usersMentionsList={usersMentionsList} handleSelect={handleSelect} />
                </div>
            </div>
            <div className={classes.formItems}>
                <AddTweetImage images={tweetImages} handleImages={handleImage} />
                {gif && <GifImage gifImage={gif.images.downsized} removeButton />}
                {quoteTweet && <Quote quoteTweet={quoteTweet} />}
                {tweetList && <TweetListComponent tweetList={tweetList} />}
                <Poll />
            </div>
            {/* <Reply isUnsentTweet={!!unsentTweet} /> */}
            <div className={classes.footer}>
                <div className={classes.footerWrapper}>
                    <UploadImages handleSetImage={(h) => {
                        setTweetImages((prev) => ([...prev, ...h]));
                    }} imageCount={tweetImages?.length} />
                    <GifIconButton />
                    <PollIconButton buttonName={buttonName} disabled={!!quoteTweet || scheduledDate !== null} />
                    <EmojiIconButton addEmoji={addEmoji} />
                    {/* <ScheduleIconButton buttonName={buttonName} disabled={!!quoteTweet} /> */}
                </div>
                <div className={classes.footerAddForm}>
                    <TextCountProgress text={text} />
                    <Button
                        onClick={
                            buttonName === "Reply"
                                ? handleClickReplyTweet
                                : quoteTweet !== undefined
                                ? handleClickQuoteTweet
                                : handleClickAddTweet
                        }
                        disabled={tweetDisabled()}
                        color="primary"
                        variant="contained"
                    >
                        {isTweetBtnDisabled ? "Uploading..." : buttonName}
                    </Button>
                </div>
            </div>
        </>
    );
};

export default AddTweetForm;
